import KeyPlayerStackAnalyzer from 'services/key-player-stacking/key-player-stack-analyzer';
import {sortArrayOfObjects} from 'helpers/common-helper';
import {ComboUnit, ComboUnitInterface} from './combo-unit';

export interface KeyPlayerStackGroups {
    usedInLineups: any[];
    unusedInLineups: any[];
}

class KeyPlayerStackGroupsBuilder {
    private readonly players;
    private readonly stacking;
    private readonly comboExpMax;
    private groupsUsedInLineups: object = {};
    private groupsUnusedInLineups: object = {};

    constructor(params) {
        this.players = params.players;
        this.stacking = params.stacking;
        this.comboExpMax = params.comboExpMax;
    }

    public getGroups(lineups): KeyPlayerStackGroups {
        this.prepareGroups(lineups);

        return {
            usedInLineups: Object.values(this.groupsUsedInLineups),
            unusedInLineups: Object.values(this.groupsUnusedInLineups),
        };
    }

    private prepareGroups(lineups) {
        this.defineGroupsUsedInLineups(lineups);
        Object.values(this.groupsUsedInLineups).map((group) => {
            group.sortedCombos = this.sortCombosByQuantity(group.combos);
        });

        this.defineGroupsUnusedInLineups();
        Object.values(this.groupsUnusedInLineups).map((group) => {
            group.sortedCombos = this.sortCombosByQuantity(group.combos);
        });
    }

    private defineGroupsUsedInLineups(lineups) {
        lineups.map((lineup) => this.defineGroupsUsedInLineup(lineup));
    }

    private defineGroupsUsedInLineup(lineup) {
        const stackAnalyzer = this.createStackAnalyzer(lineup);

        stackAnalyzer.getKeyPlayerIds().map((keyPlayerId) => {
            const stackedPlayerIds = stackAnalyzer.getStackedPlayerIdsByKeyPlayerId(keyPlayerId);
            this.setUsedGroup(lineup, keyPlayerId, stackedPlayerIds);
        });
    }

    private defineGroupsUnusedInLineups() {
        Object.keys(this.comboExpMax.all()).map((keyPlayerId) => {
            const combos = this.defineCombosOfGroupUnusedInLineups(keyPlayerId);

            this.groupsUnusedInLineups[keyPlayerId] = this.initKeyPlayerGroup(keyPlayerId);
            this.groupsUnusedInLineups[keyPlayerId].combos = combos;
        });
    }

    private defineCombosOfGroupUnusedInLineups(keyPlayerId) {
        const combos = {};
        const stackCombos = this.comboExpMax.getStackCombos(keyPlayerId);
        Object.values(stackCombos).map((stackCombo) => {
            // @ts-ignore
            const comboId = stackCombo.getId();

            if (this.groupsUsedInLineups[keyPlayerId] && comboId in this.groupsUsedInLineups[keyPlayerId].combos) {
                return;
            }
            // @ts-ignore
            combos[comboId] = ComboUnit.create(stackCombo.getCombo());
        });

        return {...combos};
    }

    private setUsedGroup(lineup, keyPlayerId, stackedPlayerIds) {
        this.groupsUsedInLineups[keyPlayerId] ??= this.initKeyPlayerGroup(keyPlayerId);
        this.groupsUsedInLineups[keyPlayerId].quantity++;

        this.setStackedPlayersToUsedGroupIfFound(lineup, keyPlayerId, stackedPlayerIds);
    }

    private setStackedPlayersToUsedGroupIfFound(lineup, keyPlayerId, stackedPlayerIds) {
        if (isEmpty(stackedPlayerIds)) {
            return;
        }

        const comboId = this.comboExpMax.doComboId(stackedPlayerIds);

        const comboUnit =
            comboId in this.groupsUsedInLineups[keyPlayerId].combos
                ? this.groupsUsedInLineups[keyPlayerId].combos[comboId]
                : ComboUnit.create(stackedPlayerIds);

        this.groupsUsedInLineups[keyPlayerId].combos[comboId] = ComboUnit.create(comboUnit.getPlayerIds(), [
            ...comboUnit.getLineupIds(),
            lineup.getId(),
        ]);
    }

    private createStackAnalyzer(lineup): KeyPlayerStackAnalyzer {
        return new KeyPlayerStackAnalyzer({
            players: this.players,
            stacking: this.stacking,
            simplexLineup: lineup,
        });
    }

    private initKeyPlayerGroup(playerId) {
        return {
            playerId: String(playerId),
            quantity: 0,
            combos: {},
        };
    }

    private sortCombosByQuantity(combos: {[comboId: string]: ComboUnitInterface}): ComboUnitInterface[] {
        return sortArrayOfObjects(Object.values(combos), 'quantity', 'desc');
    }
}

export default KeyPlayerStackGroupsBuilder;
